import IconButton from '@/global/components/Button/IconButton'
import { SelectField } from '@/global/components/SelectField'
import { TextField } from '@/global/components/TextField'
import { cn } from '@/lib/utils'
import ContentGuide from '@/global/guides/ContentGuide'
import { formatPorcentagemRegra } from '@acme/common'
import { Button } from 'global/components/Button'
import { FormField, FormFieldCheckbox } from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import Dialog from 'global/guides/Dialog'
import { ChevronDownIcon, MinusIcon, PlusIcon } from 'lucide-react'
import { FC, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { PorcentagemRegraCalculo } from 'service/api'
import { z } from 'zod'

export default function Porcentagem() {
    const { setValue, watch } = useFormContext<{
        porcentagens: PorcentagemRegraCalculo[]
        controlarBancoHoras: boolean
    }>()

    const [controlarBancoHoras, porcentagens] = watch([
        'controlarBancoHoras',
        'porcentagens',
    ])

    function handleAdd() {
        setValue(
            'porcentagens',
            [
                ...(porcentagens || []).concat({
                    porcentagem: (() => {
                        let porcentagem = 50
                        while (
                            porcentagens?.some(
                                p => p.porcentagem === String(porcentagem),
                            )
                        )
                            porcentagem++
                        return String(porcentagem)
                    })(),
                    compensacao: 100,
                    persiste: true,
                }),
            ] as PorcentagemRegraCalculo[],
            { shouldDirty: true, shouldTouch: true },
        )
    }

    function handleRemove(index: number) {
        setValue(
            'porcentagens',
            porcentagens.filter(
                (_, i) => i !== index,
            ) as PorcentagemRegraCalculo[],
            { shouldDirty: true, shouldTouch: true },
        )
    }

    function handleUpdate(
        index: number,
        value: Partial<PorcentagemRegraCalculo>,
    ) {
        setValue(
            `porcentagens.${index}`,
            { ...porcentagens[index], ...value } as PorcentagemRegraCalculo,
            { shouldDirty: true, shouldTouch: true },
        )
    }

    return (
        // Colunas pela largura do contêiner, não da tela: o formulário abre
        // num modal com largura máxima. Com banco de horas o item ganha
        // "Persiste" e precisa de mais espaço.
        <div
            className={cn(
                'mb-4 grid gap-4',
                controlarBancoHoras
                    ? 'grid-cols-[repeat(auto-fill,minmax(min(28rem,100%),1fr))]'
                    : 'grid-cols-[repeat(auto-fill,minmax(min(20rem,100%),1fr))]',
            )}
        >
            {porcentagens.map((item, index) => (
                <Item
                    key={index}
                    item={item}
                    index={index}
                    handleRemove={handleRemove}
                    handleUpdate={handleUpdate}
                    controlarBancoHoras={!!controlarBancoHoras}
                />
            ))}
            <Button
                onClick={handleAdd}
                title="Adicionar porcentagem"
                variant="outlined"
                color="success"
                className="size-full"
            >
                <PlusIcon className="size-13!" />
            </Button>
        </div>
    )
}

interface ItemProps {
    item?: PorcentagemRegraCalculo
    index: number
    handleRemove: (index: number) => void
    controlarBancoHoras: boolean
    handleUpdate: (
        index: number,
        value: Partial<PorcentagemRegraCalculo>,
    ) => void
}

const Item: FC<ItemProps> = ({
    item,
    index,
    handleRemove,
    handleUpdate,
    controlarBancoHoras,
}) => {
    if (
        !item ||
        item.porcentagem == null ||
        typeof item.compensacao !== 'number'
    ) {
        return (() => {
            handleRemove(index)
            return null
        })()
    }

    const isNumeric = !isNaN(Number(item.porcentagem))

    return (
        <ContentGuide.Card className="flex flex-row items-center gap-2 p-2 rounded-md justify-start">
            <FormField
                name={`porcentagens.${index}.porcentagem`}
                type="text"
                unHelperText
                onChange={e =>
                    handleUpdate(index, {
                        porcentagem: e.target.value,
                    })
                }
                label="Se"
                defaultValue={item.porcentagem}
                endAdornment={
                    isNumeric ? (
                        <span className="mr-2 text-muted-foreground">%</span>
                    ) : null
                }
                max={5}
                className="w-[6rem]"
                autoComplete="off"
            />
            <PopupCompensa index={index} />
            {controlarBancoHoras && (
                <FormFieldCheckbox
                    name={`porcentagens.${index}.persiste`}
                    label="Persiste"
                    endAdornment={
                        <IconeAjuda className="-ml-2">
                            Se marcado, as horas extras com essa porcentagem
                            persistirão entre as competências e serão somadas ao
                            banco de horas.
                            <br />
                            Desmarque este campo apenas se você integrar essas
                            horas extras com a folha de pagamento mensalmente.
                        </IconeAjuda>
                    }
                />
            )}
            <IconButton
                color="error"
                className="ml-auto"
                onClick={() => handleRemove(index)}
                title={`Remover regra ${formatPorcentagemRegra(
                    item.porcentagem,
                )}`}
            >
                <MinusIcon />
            </IconButton>
        </ContentGuide.Card>
    )
}

interface IPropsPopupCompensa {
    index: number
}

const porcentagensPadrao = (porcentagem?: number) => {
    const PADRAO = [-1, 0, 100]
    return porcentagem ? [...PADRAO, porcentagem] : PADRAO
}

/** Rótulo do valor de compensação gravado no formulário. */
function rotuloCompensa(
    value: number,
    porcentagemNum: number,
    isNumeric: boolean,
) {
    if (value === -1) return 'Não compensa'
    if (value === 0) return 'Hora por hora'
    if (value === 100) return 'Em dobro'
    if (isNumeric && value === porcentagemNum) return 'Percentual'

    return `${value}% (${value / 100 + 1}x)`
}

function PopupCompensa({ index }: IPropsPopupCompensa) {
    const [open, setOpen] = useState(false)
    const { watch, setValue } = useFormContext()

    const [valueWatched, porcentagemWatched] = watch([
            `porcentagens.${index}.compensacao`,
            `porcentagens.${index}.porcentagem`,
        ]),
        value = Number(valueWatched),
        porcentagemStr = String(porcentagemWatched),
        porcentagemNum = Number(porcentagemWatched),
        isNumeric = !isNaN(porcentagemNum)

    const [tmp, setTmp] = useState<string | number>(0)

    useEffect(() => {
        if (open) {
            const padrao = isNumeric
                ? porcentagensPadrao(porcentagemNum)
                : porcentagensPadrao()
            setTmp(padrao.includes(value) ? value : 'CUSTOM')
        }
    }, [open])

    return [
        <div
            key={`${index}-${value}-select`}
            className="w-[150px] select-none"
            onClick={() => setOpen(true)}
        >
            <TextField
                label="Compensa"
                unHelperText
                readOnly
                value={rotuloCompensa(value, porcentagemNum, isNumeric)}
                inputClassName="cursor-pointer [&_input]:cursor-pointer"
                endAdornment={<ChevronDownIcon className="size-4 opacity-50" />}
            />
        </div>,
        <Dialog.Root
            key={`${index}-${valueWatched}`}
            open={open}
            onClose={() => setOpen(false)}
            onSubmit={v => {
                setOpen(false)
                setValue(
                    `porcentagens.${index}.compensacao`,
                    Number(v.compensacao),
                )
            }}
            schema={z
                .object({
                    compensacao: z.string().or(z.number()).optional(),
                })
                .transform(v => ({
                    compensacao: Number(
                        typeof tmp === 'string' ? v.compensacao : tmp,
                    ),
                }))}
            width="fit"
            defaultValues={{ compensacao: Number(value) }}
        >
            <Dialog.Content>
                <SelectField
                    className="mb-4 w-68"
                    value={tmp}
                    onValueChange={setTmp}
                    options={[
                        { value: -1, label: 'Não compensar' },
                        { value: 0, label: 'Hora por hora' },
                        { value: 100, label: 'Em dobro' },
                        ...(isNumeric &&
                        !porcentagensPadrao().includes(porcentagemNum)
                            ? [
                                  {
                                      value: porcentagemNum,
                                      label: `De acordo com percentual (${porcentagemStr}%)`,
                                  },
                              ]
                            : []),
                        { value: 'CUSTOM', label: 'Outro percentual' },
                    ]}
                    label=" "
                />
                {tmp === 'CUSTOM' && (
                    <FormField
                        name="compensacao"
                        label="Porcentagem a compensar"
                        type="number"
                        shouldUnregister={!open}
                        endAdornment={
                            <span className="mr-2 text-muted-foreground">
                                %
                            </span>
                        }
                    />
                )}
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>,
    ]
}
